"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.StoryImpl = void 0;
const Contract = require("@tableau/api-external-contract-js");
const ServiceRegistry_1 = require("../Services/ServiceRegistry");
const TableauError_1 = require("../TableauError");
const ErrorHelpers_1 = require("../Utils/ErrorHelpers");
const ShortLivedDeferred_1 = require("../Utils/ShortLivedDeferred");
const SheetImpl_1 = require("./SheetImpl");
const StoryPointImpl_1 = require("./StoryPointImpl");
const StoryPointInfoImpl_1 = require("./StoryPointInfoImpl");
class StoryImpl extends SheetImpl_1.SheetImpl {
    constructor(_sheetInfoImpl, storyModel, _publishedSheetInfos, _registryId) {
        super(_sheetInfoImpl, _registryId);
        this._sheetInfoImpl = _sheetInfoImpl;
        this._publishedSheetInfos = _publishedSheetInfos;
        this._registryId = _registryId;
        this._storyPointInfoImpls = [];
        this._deferred = new ShortLivedDeferred_1.ShortLivedDeferred();
        this.initializeStory(storyModel);
    }
    initializeStory(storyModel) {
        storyModel.storyPoints.forEach((storyPointModel) => {
            const isActive = storyPointModel.index === storyModel.activeStoryPointIndex;
            const storyPointInfoImpl = new StoryPointInfoImpl_1.StoryPointInfoImpl(storyPointModel.caption, storyPointModel.index, storyPointModel.storyPointId, isActive, storyPointModel.updated, this);
            this._storyPointInfoImpls.push(storyPointInfoImpl);
            if (isActive) {
                this._activeStoryPointImpl = new StoryPointImpl_1.StoryPointImpl(storyPointInfoImpl, this._publishedSheetInfos, this._registryId, storyPointModel.containedSheetInfo);
            }
        });
    }
    updateStoryInfo(index, storyPointModel) {
        if (!this._storyPointInfoImpls) {
            return;
        }
        let storyInfoImpl = this._storyPointInfoImpls[index];
        if (storyInfoImpl.storyPointId !== storyPointModel.storyPointId) {
            throw new TableauError_1.TableauError(Contract.EmbeddingErrorCodes.StoryPointIdMismatch, `We should not be updating a story point when the IDs don't match. Existing storyPointID=${storyInfoImpl.storyPointId}, newStoryPointID=${storyPointModel.storyPointId}`);
        }
        storyInfoImpl.caption = storyPointModel.caption;
        storyInfoImpl.updated = storyPointModel.updated;
        if (this._activeStoryPointImpl.storyPointId === storyPointModel.storyPointId) {
            this._activeStoryPointImpl.updated = storyInfoImpl.updated;
        }
    }
    updateStory(storyPointModel) {
        if (!this._storyPointInfoImpls) {
            return;
        }
        this._storyPointInfoImpls.forEach((storyPointInfoImpl) => {
            const isActive = storyPointInfoImpl.storyPointId === storyPointModel.storyPointId;
            if (isActive) {
                // update the state
                storyPointInfoImpl.caption = storyPointModel.caption;
                storyPointInfoImpl.index = storyPointModel.index;
                storyPointInfoImpl.active = true;
                storyPointInfoImpl.updated = storyPointModel.updated;
                // re-initialize activeStoryPointImpl
                this._activeStoryPointImpl = new StoryPointImpl_1.StoryPointImpl(storyPointInfoImpl, this._publishedSheetInfos, this._registryId, storyPointModel.containedSheetInfo);
            }
            else {
                // set old ones to false
                storyPointInfoImpl.active = false;
            }
        });
        if (this.activeStoryPoint) {
            this._deferred.resolve(this.activeStoryPoint);
        }
    }
    get activeStoryPoint() {
        return this._activeStoryPointImpl;
    }
    get storyPointsInfo() {
        return this._storyPointInfoImpls;
    }
    get isActive() {
        return this._sheetInfoImpl.active;
    }
    get isHidden() {
        return !!this._sheetInfoImpl.isHidden;
    }
    activateNextStoryPointAsync() {
        if (this._activeStoryPointImpl.index === this._storyPointInfoImpls.length - 1) {
            return Promise.resolve(this._activeStoryPointImpl);
        }
        let promise = this._deferred.getNewPromiseOrThrowIfBusy();
        const service = ServiceRegistry_1.ApiServiceRegistry.get(this._registryId).getService("story-activation-service" /* ServiceNames.StoryActivation */);
        service.activateNextStoryPointAsync();
        return promise;
    }
    activatePreviousStoryPointAsync() {
        if (this._activeStoryPointImpl.index === 0) {
            return Promise.resolve(this._activeStoryPointImpl);
        }
        let promise = this._deferred.getNewPromiseOrThrowIfBusy();
        const service = ServiceRegistry_1.ApiServiceRegistry.get(this._registryId).getService("story-activation-service" /* ServiceNames.StoryActivation */);
        service.activatePreviousStoryPointAsync();
        return promise;
    }
    activateStoryPointAsync(index) {
        ErrorHelpers_1.ErrorHelpers.verifyParameter(index, 'index');
        ErrorHelpers_1.ErrorHelpers.verifyParameterType(index, 'number', 'index');
        if (index < 0 || index >= this._storyPointInfoImpls.length) {
            throw new TableauError_1.TableauError(Contract.EmbeddingErrorCodes.IndexOutOfRange, 'The index passed to this command is out of range.');
        }
        if (index === this._activeStoryPointImpl.index) {
            return Promise.resolve(this._activeStoryPointImpl);
        }
        let promise = this._deferred.getNewPromiseOrThrowIfBusy();
        const service = ServiceRegistry_1.ApiServiceRegistry.get(this._registryId).getService("story-activation-service" /* ServiceNames.StoryActivation */);
        service.activateStoryPointAsync(index);
        return promise;
    }
    revertStoryPointAsync(index) {
        ErrorHelpers_1.ErrorHelpers.verifyParameter(index, 'index');
        ErrorHelpers_1.ErrorHelpers.verifyParameterType(index, 'number', 'index');
        if (index < 0 || index >= this._storyPointInfoImpls.length) {
            throw new TableauError_1.TableauError(Contract.EmbeddingErrorCodes.IndexOutOfRange, 'The index passed to this command is out of range.');
        }
        const service = ServiceRegistry_1.ApiServiceRegistry.get(this._registryId).getService("story-activation-service" /* ServiceNames.StoryActivation */);
        return service.revertStoryPointAsync(index).then((response) => {
            this.updateStoryInfo(response.index, response);
            const storyPointInfoImpl = new StoryPointInfoImpl_1.StoryPointInfoImpl(response.caption, response.index, response.storyPointId, false, response.updated, this);
            return storyPointInfoImpl;
        });
    }
    clearPendingPromises() {
        if (this._deferred) {
            this._deferred.reject('All pending promises cleared');
        }
    }
}
exports.StoryImpl = StoryImpl;
//# sourceMappingURL=StoryImpl.js.map